import { memo } from 'react';
import { CheckCircle, AlertCircle, Circle, MinusCircle } from 'lucide-react';
import type { EditorTabDescriptor } from '../templateEditor/types';
import type { TabBadge } from '../utils/templateValidation';

export interface EditorTabBarProps {
  tabs: EditorTabDescriptor[];
  activeTab: string;
  onTabClick: (tab: string) => void;
  getBadge: (tab: string) => TabBadge;
}

function TabBadgeIcon({ badge }: { badge: TabBadge }) {
  if (badge === 'valid')    return <CheckCircle className="h-3.5 w-3.5 text-green-500" aria-label="valid" />;
  if (badge === 'error')    return <AlertCircle  className="h-3.5 w-3.5 text-red-500"   aria-label="error" />;
  if (badge === 'visited')  return <MinusCircle  className="h-3.5 w-3.5 text-gray-400"  aria-label="in progress" />;
  return <Circle className="h-3.5 w-3.5 text-gray-300" aria-label="not visited" />;
}

// Memoized: the authoring wizard re-renders on every template-state change
// (typing, accepting an AI suggestion, etc.). The tab bar only cares about
// `tabs`, `activeTab`, `onTabClick`, and `getBadge`. As long as the parent
// passes stable references for those props, we skip re-render entirely —
// this kills the cascade of NavLink / TabBadgeIcon / icon re-renders
// reported in the React Profiler.
function EditorTabBar({
  tabs,
  activeTab,
  onTabClick,
  getBadge,
}: EditorTabBarProps) {
  return (
    <div
      role="tablist"
      aria-label="Template editor tabs"
      className="shrink-0 flex border-b border-gray-200 bg-white px-6 overflow-x-auto"
    >
      {tabs.map(tab => {
        const badge = getBadge(tab.id);
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={isActive}
            aria-controls={`tab-panel-${tab.id}`}
            id={`tab-${tab.id}`}
            type="button"
            onClick={() => onTabClick(tab.id)}
            className={[
              'inline-flex items-center gap-1.5 px-4 py-3 text-xs font-medium border-b-2 whitespace-nowrap transition-colors',
              isActive
                ? 'border-orange-500 text-orange-700'
                : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300',
            ].join(' ')}
          >
            <TabBadgeIcon badge={badge} />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export default memo(EditorTabBar);
